import { lazy, Suspense, useEffect, useState } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  FeedbackWidget,
  type FeedbackCurrentUser,
} from './components/FeedbackWidget';
import { VisitTracker } from './components/VisitTracker';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';
import { ScrollToTop } from './components/ScrollToTop';
import { watchOpsAuth } from './lib/firebase';
import {
  persistAdminSecret,
  verifyAdminGate,
} from './lib/simulation';
import { PrivacyPolicy, TermsOfUse } from './pages/Legal';

const Home = lazy(() => import('./pages/Home').then((m) => ({ default: m.Home })));
const PipelineQualification = lazy(() =>
  import('./pages/PipelineQualification').then((m) => ({ default: m.PipelineQualification })),
);
const AdminConsole = lazy(() =>
  import('./pages/AdminConsole').then((m) => ({ default: m.AdminConsole })),
);

function RouteFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center bg-[#0B0F17]">
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-slate-700 border-t-cyan-300"
        role="status"
        aria-label="Loading"
      />
    </div>
  );
}

/** Silent `/?key=` gate — valid secrets open the admin console; invalid keys leave Home as-is. */
function AdminKeyRedirect() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const key = params.get('key');

  useEffect(() => {
    if (!key?.trim()) return;
    let cancelled = false;
    void verifyAdminGate(key.trim()).then((ok) => {
      if (cancelled || !ok) return;
      persistAdminSecret(key.trim());
      navigate(`/admin/console?key=${encodeURIComponent(key.trim())}`, { replace: true });
    });
    return () => {
      cancelled = true;
    };
  }, [key, navigate]);

  return null;
}

export function App() {
  const [currentUser, setCurrentUser] = useState<FeedbackCurrentUser | null>(null);

  useEffect(() => {
    return watchOpsAuth((user) => {
      if (!user) {
        setCurrentUser(null);
        return;
      }
      setCurrentUser({
        id: user.uid,
        email: user.email,
        name: user.displayName,
      });
    });
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <AdminKeyRedirect />
      <VisitTracker />
      <RouteErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/signup" element={<Navigate to="/audit-request" replace />} />
            <Route path="/account" element={<Navigate to="/audit-request" replace />} />
            <Route path="/docs" element={<Navigate to="/audit-request" replace />} />
            <Route path="/developers" element={<Navigate to="/audit-request" replace />} />
            <Route path="/dashboard" element={<Navigate to="/audit-request" replace />} />
            <Route path="/pipeline-qualification" element={<PipelineQualification />} />
            <Route path="/audit-request" element={<PipelineQualification />} />
            <Route path="/demo" element={<PipelineQualification />} />
            <Route path="/delivery" element={<Navigate to="/audit-request" replace />} />
            <Route path="/terms" element={<TermsOfUse />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/contact" element={<PipelineQualification />} />
            <Route path="/admin/console" element={<AdminConsole />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
      <FeedbackWidget currentUser={currentUser} />
    </BrowserRouter>
  );
}
